import 'echarts-jsx/dist/renderers/SVG';
import 'echarts-jsx/dist/components/title';
import 'echarts-jsx/dist/components/legend';
import 'echarts-jsx/dist/components/tooltip';
import 'echarts-jsx/dist/components/grid';
import 'echarts-jsx/dist/components/x-axis';
import 'echarts-jsx/dist/components/y-axis';
import 'echarts-jsx/dist/charts/line';
import 'echarts-jsx/dist/charts/pie';

import {
    BusinessTypeDistribution,
    Category,
    ClassificationTask,
    DailyStatistic,
    OverviewStatistic,
    ProblemCategoryDistribution
} from '@idea2app/ai-work-order-service';
import { Icon, Loading, Option, Select } from 'idea-react';
import { observer } from 'mobx-react';
import { Component } from 'react';
import { Badge, Card, Col, Container, Row, Table } from 'react-bootstrap';
import { Color } from 'react-bootstrap/esm/types';

import categoryStore from '../model/Category';
import classificationTaskStore from '../model/ClassificationTask';
import dashboardStore from '../model/Statistic';
import { t } from '../model/Translation';
import userStore from '../model/User';

@observer
export class DashboardPage extends Component {
    async componentDidMount() {
        await dashboardStore.getAll();
        await categoryStore.getList({}, 1, 10);
        await classificationTaskStore.getList({}, 1, 5);
    }

    renderNumber = (title: string, value: number) => (
        <div className="text-center">
            <h3 className="fs-6">{title}</h3>
            <strong className="fs-3">{value}</strong>
        </div>
    );

    renderNumberGroup = (
        icon: string,
        color: Color,
        list: [string, number][],
        end = false
    ) => (
        <li
            className={`d-flex align-items-center gap-2 ${end ? '' : 'pe-2 border-end'}`}
        >
            <Icon
                className={`rounded-circle px-2 bg-light text-${color}`}
                name={icon}
                size={2}
            />
            <ul className="list-unstyled d-flex">
                {list.map(([title, value]) => this.renderNumber(title, value))}
            </ul>
        </li>
    );

    renderStatisticsCards = ({
        totalOrders,
        sensitiveOrders,
        responsibleOrders,
        optimizedOrders,
        completedSupervisions,
        generatedSupervisionOrders
    }: OverviewStatistic) => (
        <ul className="list-unstyled d-flex gap-2">
            {this.renderNumberGroup('file-earmark-text-fill', 'primary', [
                [t('total_orders'), totalOrders]
            ])}
            {this.renderNumberGroup('file-earmark-text-fill', 'info', [
                [t('basic_orders'), Math.floor(totalOrders / 3)],
                [t('sensitive_orders'), sensitiveOrders],
                [t('responsible_orders'), responsibleOrders]
            ])}
            {this.renderNumberGroup('chat-dots-fill', 'warning', [
                [t('generated_supervision'), optimizedOrders],
                [t('resolved_orders'), completedSupervisions]
            ])}
            {this.renderNumberGroup(
                'clipboard2-pulse-fill',
                'info',
                [[t('generated_supervision'), generatedSupervisionOrders]],
                true
            )}
        </ul>
    );

    renderDailyTrendChart = (dailyStats: DailyStatistic[]) => (
        <ec-svg-renderer style={{ height: '30rem', width: '100%' }}>
            <ec-tooltip trigger="axis" />
            <ec-legend
                data={[
                    t('new_orders'),
                    t('duplicate_orders'),
                    t('sensitive_orders'),
                    t('responsible_orders')
                ]}
            />
            <ec-grid left="3%" right="4%" bottom="3%" containLabel />
            <ec-x-axis
                type="category"
                boundaryGap={false}
                data={dailyStats.map(s => s.date)}
            />
            <ec-y-axis type="value" />
            <ec-line-chart
                name={t('new_orders')}
                type="line"
                data={dailyStats.map(s => s.newOrders)}
                smooth
            />
            <ec-line-chart
                name={t('duplicate_orders')}
                type="line"
                data={dailyStats.map(s => s.duplicateOrders)}
                smooth
            />
            <ec-line-chart
                name={t('sensitive_orders')}
                type="line"
                data={dailyStats.map(s => s.sensitiveOrders)}
                smooth
            />
            <ec-line-chart
                name={t('responsible_orders')}
                type="line"
                data={dailyStats.map(s => s.responsibleOrders)}
                smooth
            />
        </ec-svg-renderer>
    );

    renderBusinessTypePieChart = (
        businessTypeDistribution: BusinessTypeDistribution[]
    ) => (
        <ec-svg-renderer style={{ height: '20rem', width: '100%' }}>
            <ec-tooltip trigger="item" formatter="{a} <br/>{b}: {c} ({d}%)" />
            <ec-legend orient="vertical" left="left" />
            <ec-pie-chart
                name={t('business_type')}
                radius="50%"
                data={businessTypeDistribution}
                emphasis={{
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                    }
                }}
            />
        </ec-svg-renderer>
    );

    renderProblemCategoryPieChart = (
        problemCategoryDistribution: ProblemCategoryDistribution[]
    ) => (
        <ec-svg-renderer style={{ height: '20rem', width: '100%' }}>
            <ec-tooltip trigger="item" formatter="{a} <br/>{b}: {c} ({d}%)" />
            <ec-legend orient="vertical" left="left" />
            <ec-pie-chart
                name={t('level1_category')}
                radius="50%"
                data={problemCategoryDistribution}
                emphasis={{
                    itemStyle: {
                        shadowBlur: 10,
                        shadowOffsetX: 0,
                        shadowColor: 'rgba(0, 0, 0, 0.5)'
                    }
                }}
            />
        </ec-svg-renderer>
    );

    renderTasksTable = (tasks: ClassificationTask[]) => (
        <Table hover responsive>
            <thead>
                <tr>
                    <th>{t('task_serial_number')}</th>
                    <th>{t('task_name')}</th>
                    <th>{t('created_at')}</th>
                    <th>{t('current_status')}</th>
                    <th>{t('operation')}</th>
                </tr>
            </thead>
            <tbody>
                {tasks
                    .slice(0, 4)
                    .map(({ id, name, createdAt, status }, index) => (
                        <tr key={id}>
                            <td>{index + 1}</td>
                            <td>{name}</td>
                            <td>{new Date(createdAt).toLocaleString()}</td>
                            <td>
                                <Badge
                                    bg={
                                        status === 'completed'
                                            ? 'success'
                                            : status === 'in_progress'
                                              ? 'warning'
                                              : 'danger'
                                    }
                                >
                                    {status === 'completed'
                                        ? t('task_completed')
                                        : status === 'in_progress'
                                          ? t('task_in_progress')
                                          : t('abnormal')}
                                </Badge>
                            </td>
                            <td>
                                <a href={`#/task/classification/${id}`}>
                                    {t('view_details')}
                                </a>
                            </td>
                        </tr>
                    ))}
            </tbody>
        </Table>
    );

    renderCategoriesTable = (categories: Category[]) => (
        <Table hover responsive>
            <thead>
                <tr>
                    <th>{t('rank')}</th>
                    <th>{t('business_type')}</th>
                    <th>{t('level1_category')}</th>
                    <th>{t('level2_category')}</th>
                    <th>{t('level3_category')}</th>
                </tr>
            </thead>
            <tbody>
                {categories.slice(0, 9).map((category, index) => (
                    <tr key={category.id}>
                        <td>{index + 1}</td>
                        <td>{category.businessType}</td>
                        <td>{category.level1Category}</td>
                        <td>{category.level2Category}</td>
                        <td>{category.level3Category}</td>
                    </tr>
                ))}
            </tbody>
        </Table>
    );

    render() {
        const { session } = userStore;
        const {
            overview,
            dailyStats,
            businessTypeDistribution,
            problemCategoryDistribution,
            downloading
        } = dashboardStore;

        const { allItems: tasks } = classificationTaskStore;
        const { allItems: categories } = categoryStore;

        return !overview ? null : (
            <Container fluid>
                {downloading > 0 && <Loading />}

                <h2 className="my-4">{t('console')}</h2>

                <Row className="mb-4">
                    <Col xs={12} lg={9}>
                        <Card body>
                            <header className="d-flex align-items-center gap-3">
                                <p className="m-0 fw-bold fs-5 flex-fill">
                                    👏{t('welcome_back')}，{session.name}！
                                </p>
                                <Select variant="outline-secondary">
                                    <Option>{t('all_branches')}</Option>
                                </Select>
                                <Select variant="outline-secondary">
                                    <Option>{t('all_power_stations')}</Option>
                                </Select>
                            </header>
                            <hr />
                            {this.renderStatisticsCards(overview)}
                            <hr />
                            <h3>{t('order_data_7days')}</h3>
                            {this.renderDailyTrendChart(dailyStats)}
                        </Card>
                    </Col>
                    <Col xs={12} lg={3}>
                        <Card body>
                            <Card.Title>
                                {t('business_type_distribution')}
                            </Card.Title>
                            {this.renderBusinessTypePieChart(
                                businessTypeDistribution
                            )}
                            <Card.Title>
                                {t('problem_category_distribution')}
                            </Card.Title>
                            {this.renderProblemCategoryPieChart(
                                problemCategoryDistribution
                            )}
                        </Card>
                    </Col>
                </Row>

                <Row className="mb-4" xs={1} md={2}>
                    <Col>
                        <Card body>
                            <header className="d-flex justify-content-between align-items-center mb-3">
                                <Card.Title as="h3" className="fs-5 m-0">
                                    {t('current_tasks')}
                                </Card.Title>
                                <a
                                    className="text-decoration-none"
                                    href="#/task"
                                >
                                    {t('view_more')}
                                </a>
                            </header>
                            {this.renderTasksTable(tasks)}
                        </Card>
                    </Col>
                    <Col>
                        <Card body>
                            <header className="d-flex justify-content-between align-items-center mb-3">
                                <Card.Title as="h3" className="fs-5 m-0">
                                    {t('work_order_classification')}
                                </Card.Title>
                                <a
                                    className="text-decoration-none"
                                    href="#/category"
                                >
                                    {t('view_more')}
                                </a>
                            </header>
                            {this.renderCategoriesTable(categories)}
                        </Card>
                    </Col>
                </Row>
            </Container>
        );
    }
}
